import * as Sentry from '@sentry/react';
import { RootDispatch } from 'storage/store';
import { API } from 'utils/Api';
import { Token } from 'utils/Token';
import { Constant, StatusCode } from 'utils/consts';
import { events, apiEvents } from 'utils/Api/Api';
import { Url } from 'utils/urls';
import { EodName } from 'utils/handleEodInfoStatus/updateEodInfoInLocalStorage';
import { kisaWelcomeName } from 'pages/Dashboard/pages/Main/components/KisaWelcome/hooks/useKisaInfo';
import { getAddressesWithoutNulls } from 'utils/getAddressesWithoutNulls';

import {
  ActivateAccount,
  ActivateAccountResponse,
  AlternativeCredentials,
  PhoneResponse,
  ResetEmailResponse,
  UnblockAccountResponse,
  UserCredentials,
  UserEmail,
  UserLoginResponse,
  MessageTypes,
  ResetPassword,
  ResetPasswordResponse,
  ChangePassword,
  ChangePasswordResponse,
  VerifyClient,
  VerifyClientResponse,
  CreateAccount,
  CreateAccountResponse,
  CreateAccountFromACP,
  CreateAccountFromACPResponse,
  ResendActivateAccount,
  TokenType,
  ValidateToken,
  ValidateTokenResponse,
  TokenStatus,
  UserResponse,
  AddressesData,
  EmailsData,
  Phone,
  Email,
  UserInfo,
  AddressResponse,
  CreatePhone,
  UpdatePhone,
  AddEmailAddressPayload,
  EmailResponse,
  EditEmailAddressPayload,
  DeletePhone,
  DeleteAddress,
  TempPasswordResponse,
  UpdateTaxOfficePayload,
  ValidateTokenData,
  UserAcceptanceDataResponse,
  AlternativeLoginMessages,
  UserLoginOrPhoneLoginType,
  AlternativeCredentialsWithEmail,
  UserEmailAndLoginOrPhone,
  CompleteActivationAccountCredentials,
  UserCompleteActivateAccountResponse,
  User,
  InconsistentPhoneData,
  UserAddress,
  UserCreateAddress,
  UpdateAddress
} from './user.model';
import { CONTACT_DETAILS } from '../../utils/featureTogglesNames';

const userEffects = (dispatch: RootDispatch): any => ({
  alternativeLoginCompleteWithEmail(
    payload: AlternativeCredentialsWithEmail<UserLoginOrPhoneLoginType>
  ) {
    const url = '/auth/account/kisa/complete';
    return API.post<
      AlternativeCredentialsWithEmail<UserLoginOrPhoneLoginType>,
      UserLoginResponse
    >(url, payload)
      .then((response) => response)
      .catch((error) => error);
  },
  alternativeLoginUser<T extends UserLoginOrPhoneLoginType>(
    credentials: AlternativeCredentials<T>
  ) {
    const url = '/auth/login/kisa';
    if (credentials.password.length > Constant.oneThousandTwentyFour) {
      this.changeKisaUserStatus(AlternativeLoginMessages.badCredentials);
      return { message: AlternativeLoginMessages.badCredentials };
    }
    this.changeKisaUserStatus(null);

    return API.post<AlternativeCredentials<T>, UserLoginResponse>(
      url,
      credentials
    )
      .then((response) => {
        const { status, message, data } = response;
        if (
          status === StatusCode.forbiddenCode &&
          message === MessageTypes.regulationsNotAccepted
        ) {
          this.changeRegulationsAcceptanceRequired(true);
        } else if (
          status === StatusCode.forbiddenCode ||
          status === StatusCode.unauthorizedCode
        ) {
          this.changeKisaUserStatus(message);
        }

        if (data?.token) {
          Token.setToken(data.token);
          this.changeUserStatus('ok');
          this.changeToken(data.token);
          this.fetchUserData();
        }
        return response;
      })
      .catch(() => {
        this.changeUserStatus(null);
        this.changeToken(null);
      });
  },
  loginUser(credentials: UserCredentials) {
    const url = '/auth/login';
    const { login, conditionsAccepted, password } = credentials;
    const payload = (() => {
      if (conditionsAccepted) {
        return { regulationsAccepted: conditionsAccepted, login, password };
      }
      return credentials;
    })();
    return API.post<UserCredentials, UserLoginResponse>(url, payload)
      .then((response) => {
        const { data } = response;

        const isAlternativeEmailLoginPage =
          window.location.pathname === Url.loginEmailUgodowi;

        if (isAlternativeEmailLoginPage) {
          this.changeKisaUserStatus(null);
        } else {
          this.changeUserStatus(null);
        }
        if (data?.token) {
          Token.setToken(data.token);
          this.changeUserStatus('ok');
          this.changeToken(data.token);
          this.fetchUserData();
        }
        return response;
      })
      .catch(() => {
        this.changeUserStatus(null);
        this.changeToken(null);
      });
  },
  async fetchUserData(): Promise<void> {
    const isContactDetailsEnabled =
      !!(await dispatch.application.getFeatureToggleState(CONTACT_DETAILS));
    if (isContactDetailsEnabled) {
      dispatch.client.fetchClientData();
      return;
    }
    API.get<undefined, UserResponse<User>>('/user')
      .then((payload) => {
        const { data } = payload;
        this.changeUserData({ data });

        if (data.id) {
          Sentry.setUser({ id: data.id.toString() });
        }
      })
      .catch(() => {
        this.changeUserStatus(null);
        this.changeToken(null);
      });
  },
  activateAccountComplete(payload: CompleteActivationAccountCredentials) {
    const url = '/auth/account/activate/complete';
    return API.post<
      CompleteActivationAccountCredentials,
      UserCompleteActivateAccountResponse
    >(url, payload)
      .then((response) => {
        const { data } = response;
        if (data?.token) {
          Token.setToken(data.token);
          this.changeUserStatus('ok');
          this.changeToken(data.token);
          this.fetchUserData();
        }
        return response;
      })
      .catch(() => {
        this.changeUserStatus(null);
        this.changeToken(null);
      });
  },

  logoutUser(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('paymentsDone');
    localStorage.removeItem('sharedDocuments');
    sessionStorage.removeItem('communicationModalAccepted');
    localStorage.removeItem(kisaWelcomeName);
    localStorage.removeItem(EodName);
    this.changeToken(null);
    this.changeUserStatus(null);
    this.clearUserData();
    dispatch({ type: 'RESET' });
    events.emit(apiEvents.WSS_CLOSE_CONNECTION);
  },
  async getToken(
    tempPassword: string
  ): Promise<TempPasswordResponse | undefined> {
    const url = `/auth/impersonation/${tempPassword}`;
    return API.get<void, TempPasswordResponse>(url)
      .then((payload) => payload)
      .catch(() => undefined);
  },
  resetPasswordSendEmail(email: string): Promise<number | undefined> {
    const url = '/auth/password/sendResetEmail';
    return API.put<UserEmail, ResetEmailResponse>(url, { email })
      .then((response) => {
        dispatch.enteredEmails.storeResetEmail(email);
        return response.status;
      })
      .catch(() => undefined);
  },
  resetPasswordViaLoginOrPhoneNumberSendEmail(
    payload: UserEmailAndLoginOrPhone
  ) {
    const url = '/auth/password/sendResetEmail/kisa';
    return API.put<UserEmailAndLoginOrPhone, ResetEmailResponse>(url, payload)
      .then((response) => {
        dispatch.enteredEmails.storeResetEmail(payload.email);
        return response.status;
      })
      .catch(() => undefined);
  },
  resetPasswordSetPassword({
    token,
    password,
    personId
  }: ResetPassword): Promise<ResetPasswordResponse | undefined> {
    const url = '/auth/password/reset';
    return API.put<ResetPassword, ResetPasswordResponse>(url, {
      token,
      password,
      personId
    })
      .then((response) => response)
      .catch(() => undefined);
  },
  changePassword(
    payload: ChangePassword
  ): Promise<ChangePasswordResponse | undefined> {
    const url = '/auth/password/change';
    return API.put<ChangePassword, ChangePasswordResponse>(url, payload)
      .then((response) => response)
      .catch(() => undefined);
  },
  verifyClient({
    sourceContractId,
    personId
  }: VerifyClient): Promise<VerifyClientResponse | undefined> {
    const url = '/auth/client/verify';
    return API.post<VerifyClient, VerifyClientResponse>(url, {
      sourceContractId,
      personId
    })
      .then((response) => response)
      .catch(() => undefined);
  },
  /* todo: during the migration to the monorepo remove validateResetPasswordToken and validateRegisterAccountToken, and use hook useTokenStatus */
  validateResetPasswordToken(
    token: string
  ): Promise<ValidateTokenData | undefined> {
    const url = '/actiontoken/validate';
    return API.post<ValidateToken, ValidateTokenResponse>(url, {
      token,
      type: TokenType.RESET_PASSWORD
    })
      .then((response) => response.data)
      .catch(() => undefined);
  },
  validateRegisterAccountToken(
    token: string
  ): Promise<TokenStatus | undefined> {
    const url = '/actiontoken/validate';
    return API.post<ValidateToken, ValidateTokenResponse>(url, {
      token,
      type: TokenType.REGISTER_ACCOUNT
    })
      .then((response) => response.data.status)
      .catch(() => undefined);
  },
  validateToken({
    token,
    type
  }: {
    token: string;
    type: TokenType;
  }): Promise<TokenStatus> {
    const url = '/actiontoken/validate';
    return API.post<ValidateToken, ValidateTokenResponse>(url, {
      token,
      type
    })
      .then((response) => response.data.status)
      .catch((err) => err);
  },
  unblockAccountSendEmail(email: string): Promise<number> {
    const unblockAccountUrl = '/auth/account/sendUnblockEmail';
    return (async () => {
      const response = await API.put<UserEmail, UnblockAccountResponse>(
        unblockAccountUrl,
        {
          email
        }
      );
      dispatch.enteredEmails.storeUnblockEmail(email);
      return response.status;
    })();
  },
  validateUnblockAccountToken(
    token: string
  ): Promise<ValidateTokenData | undefined> {
    const url = '/actiontoken/validate';
    return API.post<ValidateToken, ValidateTokenResponse>(url, {
      token,
      type: TokenType.UNBLOCK_ACCOUNT
    })
      .then((response) => response.data)
      .catch(() => undefined);
  },

  unblockAccountSetPassword({
    token,
    password,
    personId
  }: ResetPassword): Promise<ResetPasswordResponse | undefined> {
    const url = '/auth/account/unblock';
    return API.put<ResetPassword, ResetPasswordResponse>(url, {
      token,
      password,
      personId
    })
      .then((response) => response)
      .catch(() => undefined);
  },
  resetUserStatus(): void {
    this.changeUserStatus('ok');
  },
  resetKisaUserStatus(): void {
    this.changeKisaUserStatus(null);
  },
  createAccount({
    email,
    password
  }: CreateAccount): Promise<CreateAccountResponse | undefined> {
    const url = '/auth/v2/account/create';
    return API.post<CreateAccount, CreateAccountResponse>(url, {
      email,
      password
    })
      .then((response) => {
        dispatch.enteredEmails.storeRegisterEmail(email);
        return response;
      })
      .catch(() => undefined);
  },
  createAccountFromACP({
    email,
    password
  }: CreateAccountFromACP): Promise<CreateAccountResponse | undefined> {
    const url = '/acp/accounts';
    return API.post<CreateAccountFromACP, CreateAccountFromACPResponse>(url, {
      email,
      password
    })
      .then((response) => {
        dispatch.enteredEmails.storeRegisterEmail(email);
        return response;
      })
      .catch(() => undefined);
  },
  activateAccount(token: string): Promise<number | undefined> {
    const url = '/auth/account/activate';
    return API.put<ActivateAccount, ActivateAccountResponse>(url, {
      token
    })
      .then((response) => response.status)
      .catch(() => undefined);
  },
  resendActivateAccount(activationToken: string): Promise<number | undefined> {
    const url = '/auth/account/activate/resend';
    return API.put<ResendActivateAccount, ActivateAccountResponse>(url, {
      activationToken
    })
      .then((response) => response.status)
      .catch(() => undefined);
  },
  // TODO: DEPRECATED, to remove when FT RELEASE_TOGGLE_SMART_4481_CONTACT_DETAILS_FRONT will be removed
  getAddresses(): Promise<UserAddress[] | undefined> {
    const url = '/user/addresses';

    return API.get<void, UserResponse<AddressesData>>(url)
      .then((response) => getAddressesWithoutNulls(response.data.addresses))
      .catch(() => undefined);
  },
  deleteAddress({
    addressId
  }: DeleteAddress): Promise<AddressResponse | undefined> {
    const url = `/user/address/${addressId}`;

    return API.delete<DeleteAddress, AddressResponse>(url)
      .then((response) => response)
      .catch(() => undefined);
  },
  // TODO: DEPRECATED, to remove when FT RELEASE_TOGGLE_SMART_4481_CONTACT_DETAILS_FRONT will be removed
  getPhones(): Promise<Phone[] | undefined> {
    const url = '/user/phones';

    return API.get<void, UserResponse<InconsistentPhoneData>>(url)
      .then((response) =>
        response.data.phones.map((phone) => ({
          phoneNumberCountryCode: phone.phoneNumberCodeCountry,
          ...phone
        }))
      )
      .catch(() => undefined);
  },
  createPhone(payload: CreatePhone): Promise<PhoneResponse | undefined> {
    const url = '/user/phone';

    return API.post<CreatePhone, PhoneResponse>(url, payload)
      .then((response) => response)
      .catch(() => undefined);
  },
  updatePhone(payload: UpdatePhone): Promise<PhoneResponse | undefined> {
    const url = '/user/phone';

    return API.put<UpdatePhone, PhoneResponse>(url, payload)
      .then((response) => response)
      .catch(() => undefined);
  },
  deletePhone(payload: DeletePhone): Promise<PhoneResponse | undefined> {
    const url = '/user/phone';

    return API.delete<DeletePhone, PhoneResponse>(url, payload)
      .then((response) => response)
      .catch(() => undefined);
  },
  // TODO: DEPRECATED, to remove when FT RELEASE_TOGGLE_SMART_4481_CONTACT_DETAILS_FRONT will be removed
  getEmails(): Promise<Email[] | undefined> {
    const url = '/user/emails';

    return API.get<void, UserResponse<EmailsData>>(url)
      .then((response) => response.data.emails)
      .catch(() => undefined);
  },
  getInfo(): Promise<UserInfo | undefined> {
    const url = '/user/info';

    return API.get<void, UserResponse<UserInfo>>(url)
      .then((response) => response.data)
      .catch(() => undefined);
  },
  createAddress(
    address: UserCreateAddress
  ): Promise<AddressResponse | undefined> {
    const url = '/user/address';

    return API.post<UserCreateAddress, AddressResponse>(url, address)
      .then((response) => response)
      .catch(() => undefined);
  },
  updateAddress(address: UpdateAddress): Promise<AddressResponse | undefined> {
    const url = '/user/address';

    return API.put<UpdateAddress, AddressResponse>(url, address)
      .then((response) => response)
      .catch(() => undefined);
  },
  createEmail(
    payload: AddEmailAddressPayload
  ): Promise<EmailResponse | undefined> {
    const url = '/user/email';

    return API.post<AddEmailAddressPayload, EmailResponse>(url, payload)
      .then((response) => response)
      .catch((error) => error);
  },
  editEmail(
    payload: EditEmailAddressPayload
  ): Promise<EmailResponse | undefined> {
    const url = '/user/email';

    return API.put<EditEmailAddressPayload, EmailResponse>(url, payload)
      .then((response) => response)
      .catch((error) => error);
  },
  deleteEmail(emailAddressId: number) {
    const url = `/user/email/${emailAddressId}`;

    return API.delete<null, EmailResponse>(url)
      .then((response) => response)
      .catch((error) => error);
  },
  updateTaxOffice({ agreementId, taxOfficeId }: UpdateTaxOfficePayload) {
    const url = `/user/${agreementId}/taxOffice/${taxOfficeId}`;

    return API.put<any, any>(url)
      .then((response) => response)
      .catch((error) => error);
  },
  async updateAcceptanceDate() {
    const endpoint = '/user/contacts/acceptance-date';
    return API.patch<void, UserAcceptanceDataResponse>(endpoint)
      .then((response) => response)
      .catch((error) => error);
  }
});

export default userEffects;
